import { Plus, Trash2, Banknote, CreditCard, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const TIER_VALUE_TYPES = [
  { value: 'total_price', label: 'Total price for the qty (₹)', hint: 'e.g. 24 pcs for ₹1400' },
  { value: 'unit_price', label: 'Price per piece (₹)', hint: 'e.g. ₹58 per pc from 24 pcs' },
  { value: 'flat_discount', label: 'Flat ₹ off MRP per piece', hint: 'e.g. ₹30 off MRP per pc' },
  { value: 'percent', label: '% off MRP', hint: 'e.g. 35% off MRP' },
];

const PAY_MODES = [
  { key: 'cash', label: 'Cash', Icon: Banknote, cls: 'text-green-600' },
  { key: 'card', label: 'Card', Icon: CreditCard, cls: 'text-blue-600' },
  { key: 'upi', label: 'UPI', Icon: Smartphone, cls: 'text-purple-600' },
];

export const emptyTier = () => ({ min_qty: 1, value_type: 'total_price', value: 0, payment_modes: {} });

export const formatTier = (t) => {
  const fmt = (v) => (t.value_type === 'percent' ? `${v}%` : `₹${Number(v).toLocaleString()}`);
  const suffix = t.value_type === 'unit_price' ? '/pc' : t.value_type === 'flat_discount' ? ' off/pc' : t.value_type === 'percent' ? ' off' : '';
  const modes = Object.entries(t.payment_modes || {}).map(([m, v]) => `${m} ${fmt(v)}`);
  return `${Number(t.min_qty)}+ pcs → ${fmt(t.value)}${suffix}${modes.length ? ` (${modes.join(', ')})` : ''}`;
};

export const OfferTiersEditor = ({ tiers, onChange }) => {
  const update = (i, patch) => onChange(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const setMode = (i, mode, raw) => {
    const pm = { ...(tiers[i].payment_modes || {}) };
    if (raw === '' || raw == null) delete pm[mode]; else pm[mode] = parseFloat(raw) || 0;
    update(i, { payment_modes: pm });
  };

  return (
    <div className="space-y-3" data-testid="offer-tiers-editor">
      <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100 text-xs text-indigo-800">
        Each slab unlocks when the line qty reaches <b>Min Qty</b> (highest matching slab wins). Leave Cash / Card / UPI blank to use the
        default value; fill one to charge differently for that payment mode. Final rate is never above the product's Selling Price.
      </div>
      {tiers.map((t, i) => {
        const vt = TIER_VALUE_TYPES.find(v => v.value === t.value_type) || TIER_VALUE_TYPES[0];
        return (
          <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-3" data-testid={`offer-tier-${i}`}>
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-3 space-y-1">
                <Label className="text-gray-700 text-xs">Min Qty *</Label>
                <Input type="number" min="1" value={t.min_qty}
                  onChange={(e) => update(i, { min_qty: parseFloat(e.target.value) || 0 })}
                  className="bg-white border-gray-300" data-testid={`tier-min-qty-${i}`} />
              </div>
              <div className="col-span-5 space-y-1">
                <Label className="text-gray-700 text-xs">Price Type</Label>
                <Select value={t.value_type} onValueChange={(v) => update(i, { value_type: v })}>
                  <SelectTrigger className="bg-white border-gray-300" data-testid={`tier-value-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {TIER_VALUE_TYPES.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3 space-y-1">
                <Label className="text-gray-700 text-xs">Default {t.value_type === 'percent' ? '(%)' : '(₹)'} *</Label>
                <Input type="number" min="0" value={t.value || ''}
                  onChange={(e) => update(i, { value: parseFloat(e.target.value) || 0 })}
                  className="bg-white border-gray-300" data-testid={`tier-value-${i}`} />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button type="button" variant="outline" size="icon" className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => onChange(tiers.filter((_, idx) => idx !== i))} disabled={tiers.length === 1}
                  data-testid={`tier-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 mb-1.5">{vt.hint} · Override by payment mode (optional)</p>
              <div className="grid grid-cols-3 gap-3">
                {PAY_MODES.map(({ key, label, Icon, cls }) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-gray-600 text-xs flex items-center gap-1"><Icon className={`w-3.5 h-3.5 ${cls}`} /> {label}</Label>
                    <Input type="number" min="0" value={t.payment_modes?.[key] ?? ''}
                      onChange={(e) => setMode(i, key, e.target.value)} placeholder="= default"
                      className="bg-white border-gray-300" data-testid={`tier-${key}-${i}`} />
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-indigo-700 font-medium" data-testid={`tier-summary-${i}`}>{formatTier(t)}</p>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
        onClick={() => onChange([...tiers, { ...emptyTier(), min_qty: (Math.max(0, ...tiers.map(t => Number(t.min_qty) || 0)) || 0) + 1 }])}
        data-testid="tier-add-btn">
        <Plus className="w-4 h-4 mr-1" /> Add another slab
      </Button>
    </div>
  );
};

export default OfferTiersEditor;
